import type * as React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import CollectionPreferences, {
  type CollectionPreferencesProps,
} from '@cloudscape-design/components/collection-preferences';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Pagination from '@cloudscape-design/components/pagination';
import PropertyFilter, {
  type PropertyFilterProps,
} from '@cloudscape-design/components/property-filter';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type { PlatformUserRow } from '@etiquetachef/types';
import { CopyValue } from '@/components/CopyValue';
import { formatDate } from '@/lib/format';
import { useUserFilter } from './userFilter';

const PAGE_SIZE_DEFAULT = 25;

/** Ações de linha; o container decide o diálogo. */
export type UserAction =
  | 'verifyEmail'
  | 'unverifyEmail'
  | 'verifyPhone'
  | 'unverifyPhone'
  | 'role'
  | 'credential'
  | 'block'
  | 'unblock'
  | 'revokeSessions'
  | 'remove';

const CONTENT_DISPLAY_DEFAULT: readonly { id: string; visible: boolean }[] = [
  { id: 'name', visible: true },
  { id: 'email', visible: true },
  { id: 'phone', visible: true },
  { id: 'contact', visible: true },
  { id: 'tenants', visible: true },
  { id: 'lastSignIn', visible: true },
  { id: 'account', visible: true },
  { id: 'actions', visible: true },
];

type Props = {
  users: readonly PlatformUserRow[];
  total: number;
  loading: boolean;
  truncated: boolean;
  errorMessage?: string | undefined;
  query: PropertyFilterProps.Query;
  onChangeQuery: (q: PropertyFilterProps.Query) => void;
  currentPageIndex: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (size: number) => void;
  onRefresh: () => void;
  onAction: (action: UserAction, user: PlatformUserRow) => void;
};

/**
 * Tabela da tela global de Usuários (issue #257). Uma linha por pessoa; a
 * coluna "Empresas" abre o detalhe de cada tenant.
 *
 * O badge de contato é o ponto da tela: mostra, lado a lado, o que cada gate
 * enxerga — e-mail pelo Firebase Auth, telefone pelo carimbo
 * `phoneVerifiedAt`. É a informação que faltava para explicar por que alguém
 * está preso na tela de verificação.
 *
 * Origem: #257 — commit b3bc5df63, 2026-08-11 17:08 +0200.
 *
 * Contrato: controlada — a página guarda filtro e paginação, busca com
 * `useUsersSearch` e decide o diálogo de cada `UserAction`; aqui só desenha e
 * devolve eventos. `truncated` mostra o aviso de que a varredura do servidor
 * bateu o teto.
 *
 * Usado por: `routes/UsersPage.tsx`. Spec: `docs/screens/platform-users-admin.md`.
 *
 * Dados: linhas `PlatformUserRow` (`packages/types/src/platform-user-search.ts`)
 * — uma por pessoa, com `memberships[]`.
 */
export function UserList({
  users,
  total,
  loading,
  truncated,
  errorMessage,
  query,
  onChangeQuery,
  currentPageIndex,
  pageSize,
  onChangePage,
  onChangePageSize,
  onRefresh,
  onAction,
}: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const filter = useUserFilter();

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: PAGE_SIZE_DEFAULT,
    wrapLines: false,
    contentDisplay: [...CONTENT_DISPLAY_DEFAULT],
  });

  const pagesCount = Math.max(1, Math.ceil(total / pageSize));

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'name',
        header: t('adminPlatformUsers.columns.name'),
        isRowHeader: true,
        cell: (u: PlatformUserRow): React.ReactNode => (
          <div>
            <Box fontWeight="bold">{u.displayName || '—'}</Box>
            <Box fontSize="body-s" color="text-body-secondary">
              <span style={{ fontFamily: 'monospace' }}>{u.uid}</span>
            </Box>
          </div>
        ),
      },
      {
        id: 'email',
        header: t('adminPlatformUsers.columns.email'),
        cell: (u: PlatformUserRow): React.ReactNode =>
          u.email ? <CopyValue value={u.email} /> : '—',
      },
      {
        id: 'phone',
        header: t('adminPlatformUsers.columns.phone'),
        cell: (u: PlatformUserRow): React.ReactNode =>
          u.phoneE164 ? <CopyValue value={u.phoneE164} /> : '—',
      },
      {
        id: 'contact',
        header: t('adminPlatformUsers.columns.contact'),
        cell: (u: PlatformUserRow): React.ReactNode =>
          u.isStaffAccount ? (
            // STAFF não passa pelo gate (e-mail sintético, sem celular);
            // mostrar "pendente" seria alarme falso.
            <Badge color="grey">{t('adminPlatformUsers.contact.notApplicable')}</Badge>
          ) : (
            <SpaceBetween direction="horizontal" size="xxs">
              <Badge color={u.emailVerified ? 'green' : 'red'}>
                {t('adminPlatformUsers.contact.email')}
              </Badge>
              <Badge color={u.phoneVerified ? 'green' : 'red'}>
                {t('adminPlatformUsers.contact.phone')}
              </Badge>
            </SpaceBetween>
          ),
      },
      {
        id: 'tenants',
        header: t('adminPlatformUsers.columns.tenants'),
        cell: (u: PlatformUserRow): React.ReactNode =>
          u.memberships.length === 0 ? (
            '—'
          ) : (
            <SpaceBetween size="xxs">
              {u.memberships.map((m) => (
                <Link
                  key={m.tenantId}
                  href={`/tenants/${m.tenantId}`}
                  onFollow={(e): void => {
                    e.preventDefault();
                    void navigate(`/tenants/${m.tenantId}`);
                  }}
                >
                  {m.tenantName} · {t(`adminMembers.roles.${m.role}`)}
                </Link>
              ))}
            </SpaceBetween>
          ),
      },
      {
        id: 'lastSignIn',
        header: t('adminPlatformUsers.columns.lastSignIn'),
        // Fonte: `metadata.lastSignInTime` do Auth — cobre web e Android. O
        // `members.lastAccessAtMillis` só é carimbado pelo Android e mostraria
        // "nunca" para quem usa só a web.
        cell: (u: PlatformUserRow): React.ReactNode =>
          u.lastSignInAt ? formatDate(u.lastSignInAt, lang) : '—',
      },
      {
        id: 'account',
        header: t('adminPlatformUsers.columns.account'),
        cell: (u: PlatformUserRow): React.ReactNode =>
          u.disabled ? (
            <Badge color="red">{t('adminPlatformUsers.account.blocked')}</Badge>
          ) : (
            <Badge color="green">{t('adminPlatformUsers.account.active')}</Badge>
          ),
      },
      {
        id: 'actions',
        header: t('adminPlatformUsers.columns.actions'),
        cell: (u: PlatformUserRow): React.ReactNode => (
          <ButtonDropdown
            expandToViewport
            // Sem o nome, a tabela fica com N botões de nome acessível
            // idêntico ("Ações") e leitor de tela não distingue as linhas.
            ariaLabel={t('adminPlatformUsers.actions.menuFor', {
              name: u.displayName || u.email || u.uid,
            })}
            items={buildActionItems(u, t)}
            onItemClick={({ detail }): void => {
              onAction(detail.id as UserAction, u);
            }}
          >
            {t('adminPlatformUsers.columns.actions')}
          </ButtonDropdown>
        ),
      },
    ],
    [t, lang, navigate, onAction],
  );

  return (
    <SpaceBetween size="m">
      {truncated ? (
        // Nunca truncar em silêncio: sem este aviso a lista parcial leria como
        // "não existe mais ninguém".
        <Alert type="warning" header={t('adminPlatformUsers.truncated.title')}>
          {t('adminPlatformUsers.truncated.body')}
        </Alert>
      ) : null}

      <Table<PlatformUserRow>
        items={[...users]}
        columnDefinitions={columnDefinitions}
        loading={loading}
        loadingText={t('common.loading', { defaultValue: 'Carregando…' })}
        trackBy="uid"
        variant="container"
        stickyHeader
        resizableColumns
        wrapLines={preferences.wrapLines ?? false}
        columnDisplay={preferences.contentDisplay ?? CONTENT_DISPLAY_DEFAULT}
        preferences={
          <CollectionPreferences
            title={t('tenants.preferences.title')}
            confirmLabel={t('tenants.preferences.confirm')}
            cancelLabel={t('tenants.preferences.cancel')}
            preferences={preferences}
            onConfirm={({ detail }): void => {
              setPreferences(detail);
              const nextSize = detail.pageSize ?? PAGE_SIZE_DEFAULT;
              if (nextSize !== pageSize) {
                onChangePageSize(nextSize);
                onChangePage(1);
              }
            }}
            pageSizePreference={{
              title: t('tenants.preferences.pageSizeLabel'),
              options: [
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
              ],
            }}
            wrapLinesPreference={{
              label: t('tenants.preferences.wrapLinesLabel'),
              description: t('tenants.preferences.wrapLinesDescription'),
            }}
            contentDisplayPreference={{
              title: t('tenants.preferences.columnsLabel'),
              options: [
                {
                  id: 'name',
                  label: t('adminPlatformUsers.columns.name'),
                  alwaysVisible: true,
                },
                { id: 'email', label: t('adminPlatformUsers.columns.email') },
                { id: 'phone', label: t('adminPlatformUsers.columns.phone') },
                { id: 'contact', label: t('adminPlatformUsers.columns.contact') },
                { id: 'tenants', label: t('adminPlatformUsers.columns.tenants') },
                { id: 'lastSignIn', label: t('adminPlatformUsers.columns.lastSignIn') },
                { id: 'account', label: t('adminPlatformUsers.columns.account') },
                { id: 'actions', label: t('adminPlatformUsers.columns.actions') },
              ],
            }}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            {errorMessage ?? t('adminPlatformUsers.empty')}
          </Box>
        }
        header={
          <Header
            variant="h1"
            counter={`(${String(total)})`}
            description={t('adminPlatformUsers.subtitle')}
            actions={
              <Button iconName="refresh" ariaLabel={t('common.refresh')} onClick={onRefresh}>
                {t('common.refresh')}
              </Button>
            }
          >
            {t('adminPlatformUsers.title')}
          </Header>
        }
        filter={
          <PropertyFilter
            query={query}
            onChange={({ detail }): void => {
              onChangeQuery({ ...detail, operation: 'and' });
            }}
            filteringProperties={filter.filteringProperties}
            filteringOptions={filter.filteringOptions}
            i18nStrings={filter.i18nStrings}
            countText={t('adminPlatformUsers.resultCount', { count: total })}
            expandToViewport
          />
        }
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={pagesCount}
            onChange={({ detail }): void => {
              onChangePage(detail.currentPageIndex);
            }}
          />
        }
      />
    </SpaceBetween>
  );
}

/**
 * Itens do menu por linha. As ações de verificação não aparecem para STAFF:
 * o gate de contato o dispensa e o e-mail é sintético — oferecê-las seria
 * convidar a uma operação sem efeito.
 */
function buildActionItems(
  u: PlatformUserRow,
  t: (key: string) => string,
): { id: UserAction; text: string; disabled?: boolean }[] {
  const items: { id: UserAction; text: string; disabled?: boolean }[] = [];

  if (!u.isStaffAccount) {
    items.push(
      u.emailVerified
        ? { id: 'unverifyEmail', text: t('adminPlatformUsers.actions.unverifyEmail') }
        : { id: 'verifyEmail', text: t('adminPlatformUsers.actions.verifyEmail') },
      u.phoneVerified
        ? { id: 'unverifyPhone', text: t('adminPlatformUsers.actions.unverifyPhone') }
        : { id: 'verifyPhone', text: t('adminPlatformUsers.actions.verifyPhone') },
    );
  }

  items.push(
    { id: 'role', text: t('adminMembers.actions.editRole') },
    {
      id: 'credential',
      text: u.isStaffAccount
        ? t('adminMembers.actions.changePin')
        : t('adminMembers.actions.changePassword'),
    },
    { id: 'revokeSessions', text: t('adminPlatformUsers.actions.revokeSessions') },
    u.disabled
      ? { id: 'unblock', text: t('adminPlatformUsers.actions.unblock') }
      : { id: 'block', text: t('adminPlatformUsers.actions.block') },
    { id: 'remove', text: t('adminPlatformUsers.actions.removeFromTenant') },
  );

  return items;
}
